import { useState } from 'react';
import { Search, Eye, Trash2, Filter, Mail, Phone, Calendar } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Modal } from '../../ui/Modal';
import { DeleteConfirmModal } from '../../ui/DeleteConfirmModal';

interface Enquiry {
  id: number;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  date: string;
  status: 'New' | 'Replied' | 'Closed';
}

const mockEnquiries: Enquiry[] = [
  {
    id: 1,
    name: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 234 567 8900',
    subject: 'Partnership Opportunity',
    message: 'I would like to discuss a potential partnership for our upcoming project...',
    date: 'Jan 15, 2026',
    status: 'New',
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@company.com',
    phone: '+1 234 567 8901',
    subject: 'Project Consultation',
    message: 'We need expert consultation for our web application development...',
    date: 'Jan 14, 2026',
    status: 'Replied',
  },
  {
    id: 3,
    name: 'Michael Chen',
    email: 'mchen@tech.io',
    phone: '+1 234 567 8902',
    subject: 'Technical Support',
    message: 'Having issues with the deployment process, need assistance...',
    date: 'Jan 13, 2026',
    status: 'Closed',
  },
  {
    id: 4,
    name: 'Emma Wilson',
    email: 'emma.w@startup.com',
    phone: '+1 234 567 8903',
    subject: 'Service Inquiry',
    message: 'Interested in your SaaS development services. Can we schedule a call?',
    date: 'Jan 12, 2026',
    status: 'New',
  },
  {
    id: 5,
    name: 'David Lee',
    email: 'david.lee@business.com',
    phone: '+1 234 567 8904',
    subject: 'Portfolio Review',
    message: 'Would like to see more details about your recent projects...',
    date: 'Jan 11, 2026',
    status: 'Replied',
  },
  {
    id: 6,
    name: 'Jessica Brown',
    email: 'jessica.b@enterprise.com',
    phone: '+1 234 567 8905',
    subject: 'Enterprise Solution',
    message: 'Looking for an enterprise-grade solution for our organization...',
    date: 'Jan 10, 2026',
    status: 'New',
  },
  {
    id: 7,
    name: 'Robert Taylor',
    email: 'robert.t@agency.com',
    phone: '+1 234 567 8906',
    subject: 'Collaboration Request',
    message: 'Our agency would like to collaborate on future projects...',
    date: 'Jan 9, 2026',
    status: 'Closed',
  },
  {
    id: 8,
    name: 'Linda Martinez',
    email: 'linda.m@startup.io',
    phone: '+1 234 567 8907',
    subject: 'MVP Development',
    message: 'Need help building an MVP for our startup idea...',
    date: 'Jan 8, 2026',
    status: 'Replied',
  },
];

export function EnquiriesPage() {
  const [viewEnquiry, setViewEnquiry] = useState<Enquiry | null>(null);
  const [deleteEnquiry, setDeleteEnquiry] = useState<Enquiry | null>(null);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'New':
        return 'bg-blue-100 text-blue-700 hover:bg-blue-100';
      case 'Replied':
        return 'bg-green-100 text-green-700 hover:bg-green-100';
      case 'Closed':
        return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
      default:
        return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-gray-200 flex-1 max-w-md focus-within:border-blue-300 transition-all shadow-sm">
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                placeholder="Search enquiries..."
                className="bg-transparent outline-none text-sm w-full font-normal"
              />
            </div>
          </div>
          <button className="bg-white border border-gray-200 px-5 py-3 rounded-xl flex items-center gap-2 hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm active:scale-95">
            <Filter size={20} />
            <span>Filter</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-600">Total Enquiries</p>
            <p className="text-3xl font-semibold text-gray-900 mt-2">342</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-600">New</p>
            <p className="text-3xl font-semibold text-blue-600 mt-2">45</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-600">Replied</p>
            <p className="text-3xl font-semibold text-green-600 mt-2">198</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-600">Closed</p>
            <p className="text-3xl font-semibold text-gray-600 mt-2">99</p>
          </div>
        </div>

        {/* Enquiries Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Subject
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden xl:table-cell">
                    Message
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    Date
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mockEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{enquiry.name}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-600 mt-1.5">
                          <Mail size={12} />
                          <span>{enquiry.email}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                          <Phone size={12} />
                          <span>{enquiry.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium hidden lg:table-cell">
                      {enquiry.subject}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 hidden xl:table-cell max-w-xs">
                      <p className="truncate">{enquiry.message}</p>
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Calendar size={14} />
                        <span>{enquiry.date}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={getStatusVariant(enquiry.status)}>
                        {enquiry.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setViewEnquiry(enquiry)}
                          className="p-2 hover:bg-blue-50 rounded-xl transition-all text-blue-600 active:scale-95"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteEnquiry(enquiry)}
                          className="p-2 hover:bg-red-50 rounded-xl transition-all text-red-600 active:scale-95"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {viewEnquiry && (
        <Modal
          isOpen={!!viewEnquiry}
          onClose={() => setViewEnquiry(null)}
          title="Enquiry Details"
          size="lg"
        >
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Name
                </label>
                <p className="text-sm text-gray-900 mt-2 font-medium">{viewEnquiry.name}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </label>
                <div className="mt-2">
                  <Badge className={getStatusVariant(viewEnquiry.status)}>
                    {viewEnquiry.status}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Email
                </label>
                <p className="text-sm text-gray-900 mt-2">{viewEnquiry.email}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Phone
                </label>
                <p className="text-sm text-gray-900 mt-2">{viewEnquiry.phone}</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Subject
              </label>
              <p className="text-sm text-gray-900 mt-2 font-medium">{viewEnquiry.subject}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Message
              </label>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed">{viewEnquiry.message}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Date
              </label>
              <p className="text-sm text-gray-900 mt-2">{viewEnquiry.date}</p>
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                onClick={() => setViewEnquiry(null)}
                className="px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteEnquiry && (
        <DeleteConfirmModal
          isOpen={!!deleteEnquiry}
          onClose={() => setDeleteEnquiry(null)}
          onConfirm={() => console.log('Delete enquiry:', deleteEnquiry.id)}
          itemName={deleteEnquiry.name}
          itemType="enquiry"
        />
      )}
    </>
  );
}
